import { zodResolver } from "@hookform/resolvers/zod";
import { Avatar } from "@package/subframe/components/Avatar";
import { Button } from "@package/subframe/components/Button";
import { Checkbox } from "@package/subframe/components/Checkbox";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherExternalLink, FeatherEye, FeatherEyeOff } from "@subframe/core";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

import { Spinner } from "@/components/ui/spinner";
import { useRegisterUserCommandControllerRegister } from "@/generated/auth/auth";
import { signIn } from "@/lib/auth";

const userRegisterSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  terms: z.boolean().refine((val) => val === true, "You must accept the terms"),
});

type FormData = z.infer<typeof userRegisterSchema>;

export const Route = createFileRoute("/_auth/register")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    return {
      redirect: search.redirect ? String(search.redirect) : undefined,
    };
  },
  component: RegisterPage,
});

function RegisterContent() {
  const { redirect } = useSearch({ from: "/_auth/register" });
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [submittedPassword, setSubmittedPassword] = useState("");

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(userRegisterSchema),
    defaultValues: {
      terms: false,
    },
  });

  const emailValue = watch("email", "");
  const passwordValue = watch("password", "");

  const { mutate: registerUser, status } =
    useRegisterUserCommandControllerRegister({
      mutation: {
        onError: () => {
          toast.error("Your registration request failed. Please try again.");
        },
        onSuccess: (variables) => {
          signUserIn(variables.data.email as string, submittedPassword);
        },
      },
    });

  const onSubmit = (data: FormData) => {
    setSubmittedPassword(data.password);
    registerUser({
      data: {
        email: data.email.toLowerCase(),
        password: data.password,
        terms: data.terms,
      },
    });
  };

  async function signUserIn(email: string, password: string) {
    try {
      await signIn(email.toLowerCase(), password);
      navigate({ to: redirect || "/dashboard", replace: true });
    } catch {
      toast.error("Your sign in request failed. Please try again.");
    }
  }

  const isLoading = status === "pending";

  return (
    <div className="flex w-full items-center justify-center h-screen">
      {/* Left side - Testimonial */}
      <div className="flex flex-col items-start justify-end gap-6 self-stretch bg-primary-navy-800 pl-24 pr-12 pt-12 pb-32 w-1/2 mobile:hidden">
        <Avatar size="x-large" image="/images/testimonial-avatar.png">
          SK
        </Avatar>
        <div className="flex w-full flex-col items-start gap-6 max-w-[80%]">
          <span className="font-['Manrope'] text-[36px] font-[500] leading-[40px] tracking-wide text-white">
            &quot;This platform has transformed how we manage compliance at our
            nursery. The interface is intuitive and saves us countless hours
            every week.&quot;
          </span>
          <div className="flex flex-wrap items-end gap-2">
            <span className="font-['Manrope'] text-[20px] font-[700] leading-[24px] tracking-tight text-primary-rose">
              Sarah Kennedy
            </span>
            <span className="text-body font-body text-primary-rose">–</span>
            <span className="text-body font-body text-primary-rose">
              Director, Little Sprouts Nursery
            </span>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex flex-col items-center justify-center self-stretch bg-white px-12 py-12 relative w-1/2 mobile:h-auto mobile:grow mobile:shrink-0 mobile:basis-0 mobile:self-stretch mobile:px-6 mobile:py-6">
        <div className="flex w-full max-w-[384px] flex-col items-start gap-6">
          {/* Logo */}
          <div className="flex items-center gap-2 mobile:absolute mobile:left-8 mobile:top-8">
            <img
              className="h-8 flex-none object-cover"
              src="/images/logo.svg"
              alt="Logo"
            />
          </div>

          <span className="text-heading-1 font-heading-1 text-neutral-900">
            Create account
          </span>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex w-full flex-col items-start gap-6"
          >
            <TextField
              className="h-auto w-full flex-none"
              size="large"
              label="Email"
              error={!!errors.email}
              helpText={errors.email?.message}
            >
              <TextField.Input
                placeholder="Enter your email"
                type="email"
                value={emailValue}
                autoComplete="email"
                autoCapitalize="none"
                disabled={isLoading}
                {...register("email")}
              />
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              size="large"
              label="Password"
              error={!!errors.password}
              helpText={errors.password?.message}
              iconRight={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="bg-transparent border-none cursor-pointer p-0"
                >
                  {showPassword ? <FeatherEye /> : <FeatherEyeOff />}
                </button>
              }
            >
              <TextField.Input
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                value={passwordValue}
                autoComplete="new-password"
                disabled={isLoading}
                {...register("password")}
              />
            </TextField>

            <Controller
              name="terms"
              control={control}
              render={({ field: { onChange, value } }) => (
                <div className="flex flex-col gap-1">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={value}
                      onCheckedChange={onChange}
                      disabled={isLoading}
                    />
                    <span className="text-caption font-caption text-subtext-color">
                      I agree to the{" "}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-navy-700 underline"
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-navy-700 underline"
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </div>
                  {errors.terms && (
                    <span className="text-caption font-caption text-error-600">
                      {errors.terms.message}
                    </span>
                  )}
                </div>
              )}
            />

            <Button
              className="h-12 w-full flex-none"
              variant="brand-primary"
              size="large"
              type="submit"
              disabled={isLoading}
              loading={isLoading}
            >
              Create account
            </Button>
          </form>

          <div className="flex w-full items-center justify-center">
            <div className="flex flex-wrap items-start gap-1">
              <span className="text-caption font-caption text-neutral-900">
                Already have an account?
              </span>
              <Link to="/login" search={{ redirect }}>
                <LinkButton variant="brand" size="small">
                  Sign in
                </LinkButton>
              </Link>
            </div>
          </div>

          <div className="flex flex-wrap items-start justify-center gap-1">
            <span className="text-caption font-caption text-subtext-color">
              By clicking &quot;Create account&quot; you agree to the
            </span>
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              <LinkButton
                variant="brand"
                size="small"
                iconRight={<FeatherExternalLink />}
              >
                Terms of Service
              </LinkButton>
            </a>
            <span className="text-caption font-caption text-subtext-color">
              and acknowledge the
            </span>
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              <LinkButton
                variant="brand"
                size="small"
                iconRight={<FeatherExternalLink />}
              >
                Privacy Notice
              </LinkButton>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function RegisterPage() {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <Spinner />
        </div>
      }
    >
      <RegisterContent />
    </Suspense>
  );
}
